import {Autocomplete, FormControl, TextField, Typography} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import FirestoreManager from '../../api/firebaseManager';
import {useAddepar} from '../../hooks/use-adepar';
import '../../styles/plaid.css';

const FROM_ADDEPAR_OPTIONS = [
  {value: 'Not Receiving', label: 'Not Receiving'},
  {value: 'Receiving', label: 'Receiving'},
  {value: 'Rolling To Owner', label: 'Rolling To Owner'},
];

const FOREST_NOT_SENDING = 'Not Sending Data';

// JIRA-2092: default "From Addepar" value derives from the Addepar item's
// Model Type. Trusts shouldn't pass transactions through. Holding Companies
// roll up to their owner (see JIRA-2093 for the backend side). Everything
// else receives normally. Defaults are applied at render time whenever the
// stored from_addepar is null — explicit user picks (including a deliberate
// "Not Receiving") are preserved.
const getDefaultFromAddepar = modelType => {
  if (!modelType) return 'Receiving';
  const normalized = String(modelType).trim().toLowerCase();
  if (normalized === 'trust') return 'Not Receiving';
  if (normalized === 'holding company') return 'Rolling To Owner';
  return 'Receiving';
};

/** Shared Autocomplete styling: white background (matches BridgeFTModal / new-user-modal pattern) */
const autocompleteSx = {
  '& .MuiFilledInput-root': {backgroundColor: 'white'},
};
const autocompletePaperSx = {backgroundColor: 'white'};

/**
 * Flatten the portfolio tree into table rows.
 * Each node becomes one row; we keep:
 * - full path (addeparStructure) for keys/mappings
 * - name and depth for display so we don't repeat the full hierarchy every row
 * - isParent: rows with children get grey background + bold; leaf rows stay white
 */
function flattenPortfolioTree(
  nodes,
  pathParts = [],
  parentName = null,
  rowId = {current: 0},
) {
  const rows = [];
  if (!Array.isArray(nodes)) return rows;

  for (const node of nodes) {
    const name = node.name ?? '—';
    const path = pathParts.length ? [...pathParts, name].join(' › ') : name;
    const columns = node.columns || {};
    const entityId = node.entity_id ?? columns.node_id ?? null;
    const ownerId = columns.direct_owner_id ?? null;
    // JIRA-2092: model_type drives the default "From Addepar" value. Read it
    // from columns; expects view 330 in Addepar to include the model_type
    // column. If absent, getDefaultFromAddepar() falls back to "Receiving".
    const modelType = columns.model_type ?? null;
    const depth = pathParts.length; // 0 = root, 1 = child, etc.
    const hasChildren =
      Array.isArray(node.children) && node.children.length > 0;

    rows.push({
      key: `row-${rowId.current++}`,
      addeparStructure: path, // full hierarchy path for mapping keys
      name,
      depth,
      ownerId: ownerId != null ? String(ownerId) : '—',
      entityId: entityId != null ? String(entityId) : '—',
      // JIRA-2093: stored on the mapping so the backend can replace the
      // security Name with the owner's name for "Rolling To Owner" items
      // without needing an extra Addepar API call per transaction.
      ownerName: parentName,
      modelType,
      isParent: hasChildren,
    });

    if (hasChildren) {
      rows.push(
        ...flattenPortfolioTree(
          node.children,
          [...pathParts, name],
          name,
          rowId,
        ),
      );
    }
  }
  return rows;
}

/**
 * Extract flattened rows from API response.
 * Response shape: { success, data: { meta, data: { attributes: { total: { children[] } } } } }
 */
function getRowsFromResponse(response) {
  if (!response || response === false) return [];
  const payload = response.success === true ? response.data : response;
  const total = payload?.data?.attributes?.total;
  const children = total?.children;
  if (!Array.isArray(children)) return [];
  return flattenPortfolioTree(children);
}

/**
 * Parse Forest accounts from addepar-assetLiabilities response.
 * Shape: { data: [...], RowCount, Rows: [{ Id, Name, ... }] }
 */
function getForestAccountOptions(apiResult) {
  if (!apiResult || !Array.isArray(apiResult.data.Rows)) return [];
  return apiResult.data.Rows.map(r => ({value: r.Id, label: r.Name ?? r.Id}));
}

/**
 * Identity match between a saved account mapping and a portfolio row.
 *
 * Keyed on entity_id + portfolio_id + owner_id. owner_id is required because
 * the same Addepar entity (e.g. a security like "Apple") appears in the tree
 * multiple times under different owners/accounts — owner_id is what
 * distinguishes those occurrences, so dropping it makes one saved mapping bleed
 * onto every occurrence of the entity.
 *
 * @param {Object} am - a stored account mapping
 * @param {string|null} entityIdFromApi - the row's entity id (null if absent)
 * @param {*} portfolioId - the entity mapping's portfolio/entity id
 * @param {*} ownerId - the row's owner id ('—' when absent)
 */
function mappingMatchesRow(am, entityIdFromApi, portfolioId, ownerId) {
  if (!am) return false;
  const sameEntity = String(am.entity_id) === String(entityIdFromApi ?? '');
  const samePortfolio =
    (am.portfolio_id != null ? String(am.portfolio_id) : null) ===
    String(portfolioId ?? '');
  const sameOwner =
    String(am.owner_id ?? '') === String(ownerId === '—' ? '' : ownerId ?? '');
  return sameEntity && samePortfolio && sameOwner;
}

export default function AddeparMappingsPage({user}) {
  const navigate = useNavigate();
  const {connection_id, entity_id} = useParams();
  const {
    state: {connections},
    editConnection,
  } = useAddepar();
  const connection = connections[connection_id] || {};
  const entity_mappings = connection.entity_mappings || [];
  const account_mappings = useMemo(
    () => connection.account_mappings || [],
    [connection.account_mappings],
  );
  const entity =
    entity_mappings.find(e => String(e.entity_id) === String(entity_id)) || {};
  // Fall back to the data on account_mappings when this entity isn't present in
  // entity_mappings (e.g. a connection whose entity_mappings were lost but which
  // still has account_mappings under this portfolio_id). Without this the page
  // can't resolve an entity name, loadData is gated off, and it hangs on
  // "Loading…". portfolio_id on the mappings is the same id as the URL entity_id.
  const fallbackMapping = account_mappings.find(
    am => String(am.portfolio_id ?? am.entity_id) === String(entity_id),
  );
  const entity_name = entity.entity_name || fallbackMapping?.entity_name || '';
  // The portfolio/entity-mapping id used to match saved mappings; fall back to
  // the URL entity_id when the entity isn't in entity_mappings.
  const portfolioId = entity.entity_id ?? entity_id;
  const customer_name = connection.customerID || '';

  // console.log({customer_name, entity_id, connection});

  const [portfolioSummary, setPortfolioSummary] = useState([]);
  const [forestAccountOptions, setForestAccountOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [portfolioRes, forestRes] = await Promise.all([
      FirestoreManager.getPortfolioSummary({
        connection_id,
        portfolio_id: entity_id,
      }),
      FirestoreManager.getAssetLiabilities({
        clientName: customer_name,
        entityName: entity_name,
        accountClassCodes: [1, 2],
      }),
    ]);

    setLoading(false);
    if (portfolioRes === false) {
      setError('Failed to load portfolio summary');
      return;
    }
    setPortfolioSummary(getRowsFromResponse(portfolioRes));

    // On failure the response is {success:false, error, data:null}, so reach
    // for .data.Rows defensively. Reading it directly threw a TypeError and
    // took the whole page down, which is how a Forest API problem showed up as
    // an unusable mapping screen rather than an empty dropdown.
    if (Array.isArray(forestRes?.data?.Rows)) {
      setForestAccountOptions(getForestAccountOptions(forestRes));
    } else {
      setForestAccountOptions([]);
      // Say so, otherwise an empty account list is indistinguishable from a
      // customer that genuinely has no accounts.
      setError(
        forestRes?.error ?
          `Could not load Forest accounts: ${forestRes.error}` :
          'Could not load Forest accounts for this entity.',
      );
    }
  }, [connection_id, customer_name, entity_name, entity_id]);

  useEffect(() => {
    if (!connection_id || !entity_name) return;
    loadData();
  }, [connection_id, entity_name, loadData]);

  // Working copy of account_mappings. Updated synchronously on every edit so
  // rapid successive edits never build on a stale render-time snapshot, and
  // writes are serialized so out-of-order network resolution can't clobber a
  // newer edit with an older one.
  //
  // BUGFIX: previously each edit rebuilt the entire array from render-time
  // state and wrote the whole connection back. Fast consecutive edits (common
  // when mapping a freshly connected entity) read the same stale base and
  // overwrote each other, so mappings were silently lost.
  const workingMappingsRef = useRef(account_mappings);
  const pendingWritesRef = useRef(0);
  const writeChainRef = useRef(Promise.resolve());

  // Re-seed the working copy from the source of truth (e.g. on load, or a
  // change from elsewhere) only when no write is in flight, so resolving saves
  // don't reset it to a stale intermediate value mid-editing.
  useEffect(() => {
    if (pendingWritesRef.current === 0) {
      workingMappingsRef.current = account_mappings;
    }
  }, [account_mappings]);

  const getMappingForRow = row => {
    const entityIdFromApi =
      row.entityId && row.entityId !== '—' ? String(row.entityId) : null;
    const portfolioIdFromEntityMapping = portfolioId;

    const m = account_mappings.find(am =>
      mappingMatchesRow(
        am,
        entityIdFromApi,
        portfolioIdFromEntityMapping,
        row.ownerId,
      ),
    );

    return {
      forestAccountId: m?.forest_account_id ?? null,
      // JIRA-2092: if the user has never set a value, use the model-type
      // derived default. Once they pick anything explicitly, the stored
      // value wins.
      fromAddepar: m?.from_addepar ?? getDefaultFromAddepar(row.modelType),
    };
  };

  const updateMapping = (row, field, value, forestAccountName = null) => {
    const {ownerId, entityId, ownerName, modelType} = row;
    const portfolioIdFromEntityMapping = portfolioId;
    // entity_name (fallback-aware) comes from the component scope.
    const entityIdFromApi =
      entityId && entityId !== '—' ? String(entityId) : null;

    // Map UI field names to stored keys
    const storageField =
      field === 'forestAccountId'
        ? 'forest_account_id'
        : field === 'fromAddepar'
          ? 'from_addepar'
          : field;

    // Build against the working copy (latest, including any in-flight edits),
    // not the render-time snapshot.
    const current = workingMappingsRef.current;
    const existingIndex = current.findIndex(am =>
      mappingMatchesRow(am, entityIdFromApi, portfolioIdFromEntityMapping, ownerId),
    );

    const base = existingIndex >= 0 ? current[existingIndex] : {};

    // account_name must be the Forest account name (APIs expect it), not Addepar
    const accountName =
      storageField === 'forest_account_id'
        ? (forestAccountName ?? null)
        : (base.account_name ?? null);

    let nextRecord = {
      ...base,
      // Core fields other APIs expect
      account_name: accountName,
      entity_id: entityIdFromApi,
      entity_name,
      owner_id: ownerId,
      // JIRA-2093: owner_name is read by the backend when applying the
      // "Rolling To Owner" rule so it can replace the security Name on
      // those transactions. Captured here from the parent node in the
      // portfolio tree. model_type is stored alongside for visibility /
      // debugging — the backend doesn't currently rely on it.
      owner_name: ownerName ?? base.owner_name ?? null,
      model_type: modelType ?? base.model_type ?? null,
      portfolio_id: portfolioIdFromEntityMapping,
    };

    if (storageField === 'forest_account_id') {
      if (value === FOREST_NOT_SENDING || !value) {
        nextRecord.forest_account_id = null;
      } else {
        nextRecord.forest_account_id = value;
      }
    } else if (storageField === 'from_addepar') {
      nextRecord.from_addepar = value || 'Not Receiving';
    } else {
      nextRecord[storageField] = value;
    }

    if (nextRecord.from_addepar == null) {
      nextRecord.from_addepar = 'Not Receiving';
    }

    const nextAccountMappings =
      existingIndex >= 0
        ? current.map((am, idx) => (idx === existingIndex ? nextRecord : am))
        : [...current, nextRecord];

    // Update the working copy synchronously so the next edit builds on it...
    workingMappingsRef.current = nextAccountMappings;

    // ...and serialize the write. Each queued save flushes the latest working
    // copy and writes ONLY account_mappings (merge), so saves can't clobber
    // each other or overwrite tokens/other fields the backend refreshes.
    pendingWritesRef.current += 1;
    writeChainRef.current = writeChainRef.current
      .catch(() => {})
      .then(async () => {
        const {success} = await editConnection({
          id: connection.id || connection_id,
          account_mappings: workingMappingsRef.current,
        });
        if (!success) {
          window.alert('Failed to save mapping.');
        }
      })
      .finally(() => {
        pendingWritesRef.current -= 1;
      });
  };

  return (
    <div className='page-container'>
      <div
        className='flex-row justify-between'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <div style={{flexGrow: 1}}>
          <Typography variant='h6' style={{marginBottom: '12px'}}>
            Addepar Mappings – {entity_name || 'Entity'}
          </Typography>
          {loading && <Typography color='textSecondary'>Loading...</Typography>}
          {error && (
            <Typography color='error' style={{marginBottom: '12px'}}>
              {error}
            </Typography>
          )}
          {!loading && !error && (
            <div className='addepar-mappings-table' style={{overflowX: 'auto'}}>
              <table style={{width: '100%'}}>
                <thead>
                  <tr>
                    <th>Forest To Addepar Account</th>
                    <th>Addepar Structure</th>
                    <th>Owner Id</th>
                    <th>Entity Id</th>
                    <th>From Addepar</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioSummary.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{textAlign: 'center', padding: 16}}
                      >
                        No portfolio data. Check connection and try again.
                      </td>
                    </tr>
                  ) : (
                    portfolioSummary.map((row, rowIndex) => {
                      const {forestAccountId, fromAddepar} =
                        getMappingForRow(row);
                      const hasOwnerId =
                        row.ownerId && row.ownerId !== '—';
                      return (
                        <tr
                          key={row.key}
                          style={
                            row.isParent
                              ? {backgroundColor: '#f5f6f8'}
                              : undefined
                          }
                        >
                          <td>
                            <FormControl fullWidth size='small'>
                              <Autocomplete
                                disabled={!hasOwnerId}
                                options={[
                                  {
                                    value: FOREST_NOT_SENDING,
                                    label: FOREST_NOT_SENDING,
                                  },
                                  ...forestAccountOptions,
                                ]}
                                value={
                                  forestAccountId == null ||
                                  forestAccountId === ''
                                    ? {
                                        value: FOREST_NOT_SENDING,
                                        label: FOREST_NOT_SENDING,
                                      }
                                    : (forestAccountOptions.find(
                                        o => o.value === forestAccountId,
                                      ) ?? {
                                        value: forestAccountId,
                                        label: forestAccountId,
                                      })
                                }
                                onChange={(_, newValue) =>
                                  updateMapping(
                                    row,
                                    'forestAccountId',
                                    newValue?.value ?? null,
                                    newValue?.value &&
                                      newValue.value !== FOREST_NOT_SENDING
                                      ? (newValue?.label ?? null)
                                      : null,
                                  )
                                }
                                getOptionLabel={opt => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) =>
                                  a?.value === b?.value
                                }
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    variant='filled'
                                    size='small'
                                    sx={autocompleteSx}
                                    inputProps={{
                                      ...params.inputProps,
                                      'data-mapping-row-index': rowIndex,
                                      'data-mapping-col': 'forest',
                                      onFocus: e => {
                                        if (params.inputProps.onFocus) {
                                          params.inputProps.onFocus(e);
                                        }
                                        e.target.select();
                                      },
                                      onKeyDown: e => {
                                        // Preserve Autocomplete's internal key handling
                                        if (params.inputProps.onKeyDown) {
                                          params.inputProps.onKeyDown(e);
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const next = document.querySelector(
                                            `[data-mapping-row-index='${rowIndex + 1}'][data-mapping-col='forest']`,
                                          );
                                          if (next instanceof HTMLInputElement) {
                                            next.focus();
                                            setTimeout(() => next.select(), 50);
                                          }
                                        }
                                      },
                                    }}
                                  />
                                )}
                                slotProps={{
                                  paper: {sx: autocompletePaperSx},
                                }}
                              />
                            </FormControl>
                          </td>
                          <td>
                            <div
                              style={{
                                paddingLeft: `${(row.depth || 0) * 24}px`,
                                whiteSpace: 'nowrap',
                                fontWeight: row.isParent ? 600 : 400,
                              }}
                            >
                              {row.name || row.addeparStructure}
                            </div>
                          </td>
                          <td>{row.ownerId}</td>
                          <td>{row.entityId}</td>
                          <td>
                            <FormControl fullWidth size='small'>
                              <Autocomplete
                                disabled={!hasOwnerId}
                                options={FROM_ADDEPAR_OPTIONS}
                                value={
                                  FROM_ADDEPAR_OPTIONS.find(
                                    o => o.value === fromAddepar,
                                  ) ?? {
                                    value: fromAddepar,
                                    label: fromAddepar,
                                  }
                                }
                                onChange={(_, newValue) =>
                                  updateMapping(
                                    row,
                                    'fromAddepar',
                                    newValue?.value ?? 'Not Receiving',
                                  )
                                }
                                getOptionLabel={opt => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) =>
                                  a?.value === b?.value
                                }
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    variant='filled'
                                    size='small'
                                    sx={autocompleteSx}
                                    inputProps={{
                                      ...params.inputProps,
                                      'data-mapping-row-index': rowIndex,
                                      'data-mapping-col': 'from',
                                      onFocus: e => {
                                        if (params.inputProps.onFocus) {
                                          params.inputProps.onFocus(e);
                                        }
                                        e.target.select();
                                      },
                                      onKeyDown: e => {
                                        if (params.inputProps.onKeyDown) {
                                          params.inputProps.onKeyDown(e);
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const next = document.querySelector(
                                            `[data-mapping-row-index='${rowIndex + 1}'][data-mapping-col='from']`,
                                          );
                                          if (next instanceof HTMLInputElement) {
                                            next.focus();
                                            setTimeout(() => next.select(), 50);
                                          }
                                        }
                                      },
                                    }}
                                  />
                                )}
                                slotProps={{
                                  paper: {sx: autocompletePaperSx},
                                }}
                              />
                            </FormControl>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            marginLeft: '36px',
          }}
        >
          <button
            className='disconnect'
            onClick={() => navigate(`/addepar/${connection_id}`)}
            style={{marginBottom: '16px'}}
          >
            Back to Entities
          </button>
          <button
            className='disconnect'
            onClick={() => navigate(-1)}
            style={{marginBottom: '16px'}}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
